// frontend/src/pages/RestDayMonitoring/components/EmployeeRestDayDetailModal.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Calendar,
  AlertTriangle,
  ShieldCheck,
  Clock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Modal from '../../../components/UI/Modal';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import { TradeBadge } from '../../../components/UI/TradeBadge';
import {
  getDepartmentBadgeColor,
  getDepartmentShortName,
} from '../../../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../../../config/tradeConfig';
import { formatDate, formatDateForInput, getTodayInSingaporeTimezone } from '../../../utils/dateUtils';

// ─── Helpers ────────────────────────────────────────────────────────────────────

const getStatusLabel = (status) => {
  const labels = {
    compliant: 'OK',
    watch: 'Watch',
    warning: 'Warning',
    critical: 'Critical',
    non_compliant: 'Violated',
  };
  return labels[status] || 'OK';
};

const getStatusColor = (status) => {
  const colors = {
    compliant: 'text-green-600',
    watch: 'text-amber-600',
    warning: 'text-orange-600',
    critical: 'text-red-600',
    non_compliant: 'text-red-700',
  };
  return colors[status] || 'text-green-600';
};

// ─── Day Type Config ────────────────────────────────────────────────────────────

const dayTypeConfig = {
  work: { bg: 'bg-red-500', text: 'text-white', label: 'Work', ringClass: '' },
  rest: { bg: 'bg-green-500', text: 'text-white', label: 'Rest', ringClass: '' },
  leave: { bg: 'bg-blue-400', text: 'text-white', label: 'Leave', ringClass: '' },
  public_holiday: { bg: 'bg-purple-500', text: 'text-white', label: 'PH', ringClass: '' },
  weekend: { bg: 'bg-green-400', text: 'text-white', label: 'W/E', ringClass: '' },
  unknown: { bg: 'bg-gray-300', text: 'text-gray-600', label: '?', ringClass: '' },
};

// ─── Calendar Grid Component ────────────────────────────────────────────────────

// Normalize API status values to dayTypeConfig keys
const normalizeDayStatus = (status) => {
  const map = { worked: 'work', no_data: 'unknown' };
  return map[status] || status;
};

const TimelineCalendar = ({ timeline }) => {
  if (!timeline || timeline.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-400">
        No timeline data available
      </div>
    );
  }

  // Group by week rows (7 columns: Mon-Sun)
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Build a map for quick lookup
  const dayMap = {};
  timeline.forEach(day => {
    dayMap[day.date] = day;
  });

  // Determine date range
  const startDate = new Date(timeline[0].date + 'T00:00:00');
  const endDate = new Date(timeline[timeline.length - 1].date + 'T00:00:00');

  // Adjust start to Monday
  const adjustedStart = new Date(startDate);
  const startDow = adjustedStart.getDay();
  const mondayOffset = startDow === 0 ? -6 : 1 - startDow;
  adjustedStart.setDate(adjustedStart.getDate() + mondayOffset);

  // Build weeks
  const weeks = [];
  const cursor = new Date(adjustedStart);
  while (cursor <= endDate || cursor.getDay() !== 1) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const dateStr = formatDateForInput(cursor);
      const dayData = dayMap[dateStr] || null;
      const isInRange = cursor >= startDate && cursor <= endDate;
      week.push({ date: dateStr, data: dayData, isInRange });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
    if (cursor > endDate && cursor.getDay() === 1) break;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {weekDays.map(day => (
              <th key={day} className="text-[10px] font-medium text-gray-500 pb-1 text-center w-[14%]">
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((cell, ci) => {
                const dayData = cell.data;
                const dayType = normalizeDayStatus(dayData?.status) || 'unknown';
                const config = dayTypeConfig[dayType] || dayTypeConfig.unknown;
                const dayNum = new Date(cell.date + 'T00:00:00').getDate();
                const isToday = cell.date === getTodayInSingaporeTimezone();
                
                return (
                  <td key={ci} className="px-0.5 py-px text-center">
                    {cell.isInRange ? (
                      <div
                        className={`w-full h-7 rounded flex items-center justify-center ${config.bg} ${config.text} ${
                          isToday ? 'ring-2 ring-offset-1 ring-gray-900' : ''
                        }`}
                        title={`${formatDate(cell.date)}: ${config.label}${dayData?.notes ? ' — ' + dayData.notes : ''}`}
                      >
                        <span className="text-[10px] font-bold leading-none">{dayNum}</span>
                      </div>
                    ) : (
                      <div className="w-full h-7 rounded bg-gray-50" />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500 flex-wrap">
        {Object.entries(dayTypeConfig).filter(([k]) => k !== 'unknown').map(([key, cfg]) => (
          <span key={key} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-sm ${cfg.bg}`} /> {cfg.label}
          </span>
        ))}
        <span className="flex items-center gap-1 ml-2">
          <span className="w-2 h-2 rounded-sm bg-gray-100 ring-2 ring-gray-900 ring-offset-1" /> Today
        </span>
      </div>
    </div>
  );
};

// ─── Main Modal Component ───────────────────────────────────────────────────────

const EmployeeRestDayDetailModal = ({
  isOpen,
  onClose,
  employee,
  getEmployeeDetail,
}) => {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);

  // Fetch detail data
  const fetchDetail = useCallback(async () => {
    if (!employee?.staffId) return;
    setLoadingDetail(true);
    const result = await getEmployeeDetail(employee.staffId);
    if (result) {
      setDetail(result);
    }
    setLoadingDetail(false);
  }, [employee?.staffId, getEmployeeDetail]);

  useEffect(() => {
    if (isOpen && employee) {
      fetchDetail();
    }
    if (!isOpen) {
      setDetail(null);
      setLoadingDetail(true);
    }
  }, [isOpen, employee, fetchDetail]);

  if (!isOpen || !employee) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      theme="admin"
      contentClassName="space-y-6"
      customHeader={(
        <div className="modal-header flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-red-600">
                {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{employee.fullName}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-sm text-gray-500">{employee.staffId}</span>
                {employee.department && employee.department !== 'Unassigned' ? (
                  <InfoBadge
                    color={getDepartmentBadgeColor(employee.department)}
                    label={getDepartmentShortName(employee.department)}
                    title={employee.department}
                    size="xs"
                  />
                ) : (
                  <InfoBadge color="gray" label="Unassigned" size="xs" />
                )}
                {employee.trade &&
                  employee.trade !== 'No Trade' &&
                  !isTradeRedundantWithDepartment(
                    employee.department,
                    null,
                    null,
                    employee.trade,
                  ) && (
                  <TradeBadge trade={employee.trade} size="xs" />
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="modal-close-admin" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      footer={(
        <button onClick={onClose} className="btn btn-admin-outline">
          Close
        </button>
      )}
    >

            {/* ── 1. Current Status Summary ───────────────────────── */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-4">
                <ShieldCheck className="w-4 h-4 mr-2 text-red-600" />
                Compliance Status
              </h4>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Consecutive Days</p>
                  <p className={`text-lg font-bold ${employee.consecutiveWorkingDays >= 10 ? 'text-red-600' : 'text-gray-900'}`}>
                    {employee.consecutiveWorkingDays}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Status</p>
                  <p className={`text-lg font-bold ${getStatusColor(employee.complianceStatus)}`}>
                    {getStatusLabel(employee.complianceStatus)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Last Rest Day</p>
                  <p className="text-lg font-bold text-gray-900">
                    {employee.lastRestDayDate 
                      ? new Date(employee.lastRestDayDate + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short' })
                      : '—'
                    }
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Rest Required By</p>
                  {(() => {
                    const restBy =
                      detail?.projection?.requiredRestBy ||
                      employee.requiredRestBy ||
                      null;
                    if (!restBy) {
                      return (
                        <p className="text-lg font-bold text-gray-900">
                          {Math.max(0, 12 - employee.consecutiveWorkingDays)} days
                        </p>
                      );
                    }
                    const restDate = new Date(restBy + 'T00:00:00');
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const diffDays = Math.round(
                      (restDate - today) / (1000 * 60 * 60 * 24)
                    );
                    let colorClass = 'text-gray-900';
                    if (employee.consecutiveWorkingDays >= 13 || diffDays < 0) {
                      colorClass = 'text-red-600';
                    } else if (diffDays === 0) {
                      colorClass = 'text-red-500';
                    } else if (diffDays === 1) {
                      colorClass = 'text-orange-600';
                    } else if (diffDays <= 2) {
                      colorClass = 'text-red-600';
                    }
                    return (
                      <>
                        <p className={`text-base font-bold ${colorClass}`}>
                          {formatDate(restBy)}
                        </p>
                        <p className={`text-[11px] font-medium mt-0.5 ${colorClass}`}>
                          {diffDays < 0
                            ? 'Overdue'
                            : diffDays === 0
                              ? 'Today'
                              : diffDays === 1
                                ? 'Tomorrow'
                                : `${diffDays} days left`}
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Streak Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{employee.consecutiveWorkingDays} of 12 days</span>
                  <span>{Math.round((employee.consecutiveWorkingDays / 12) * 100)}%</span>
                </div>
                <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full ${
                      employee.consecutiveWorkingDays >= 13 ? 'bg-red-500' :
                      employee.consecutiveWorkingDays >= 12 ? 'bg-red-400' :
                      employee.consecutiveWorkingDays >= 10 ? 'bg-orange-500' :
                      employee.consecutiveWorkingDays >= 7 ? 'bg-amber-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, (employee.consecutiveWorkingDays / 12) * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* ── 1b. Rest-Day Projection ─────────────────────────── */}
            {detail?.projection?.requiredRestBy && (
              <div className={`rounded-xl p-5 border ${
                detail.projection.projectedBreach
                  ? 'bg-red-50 border-red-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                  <Calendar className={`w-4 h-4 mr-2 ${detail.projection.projectedBreach ? 'text-red-600' : 'text-green-600'}`} />
                  Rest Day Projection
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Rest Day Required By</p>
                    {(() => {
                      const restBy = detail.projection.requiredRestBy;
                      const restDate = new Date(restBy + 'T00:00:00');
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const diffDays = Math.round(
                        (restDate - today) / (1000 * 60 * 60 * 24)
                      );
                      const colorClass =
                        employee.consecutiveWorkingDays >= 13 || diffDays < 0
                          ? 'text-red-600'
                          : diffDays === 0
                            ? 'text-red-500'
                            : diffDays === 1
                              ? 'text-orange-600'
                              : 'text-gray-900';
                      const relative =
                        diffDays < 0
                          ? 'Overdue'
                          : diffDays === 0
                            ? 'Today'
                            : diffDays === 1
                              ? 'Tomorrow'
                              : `${diffDays} days left`;
                      return (
                        <>
                          <p className={`text-base font-bold ${colorClass}`}>
                            {formatDate(restBy)}
                          </p>
                          <p className={`text-[11px] font-medium mt-0.5 ${colorClass}`}>
                            {relative}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            Latest permissible rest day under the MOM 12-day limit
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">Projected Next Rest Day</p>
                    <p className="text-base font-bold text-gray-900">
                      {detail.projection.projectedNextRestDate
                        ? formatDate(detail.projection.projectedNextRestDate)
                        : 'None scheduled'}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Based on schedule, approved leave &amp; public holidays
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2">
                  {detail.projection.projectedBreach ? (
                    <>
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">
                        Based on their current schedule, no rest day falls before the limit.
                        Assign one on or before <strong>{formatDate(detail.projection.requiredRestBy)}</strong> to stay compliant.
                      </p>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-green-700">
                        On track — the next rest day falls within the MOM 12-day limit.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── 2. 30-Day Calendar Timeline ─────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-2">
                <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                30-Day Work/Rest Timeline
              </h4>
              
              {loadingDetail ? (
                <div className="h-48 bg-gray-50 rounded-lg animate-pulse flex items-center justify-center">
                  <div className="text-sm text-gray-400">Loading timeline...</div>
                </div>
              ) : (
                <TimelineCalendar timeline={detail?.calendarDays || []} />
              )}
            </div>

            {/* ── 3. Alert History ────────────────────────────────── */}
            {detail?.recentAlerts && detail.recentAlerts.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                  <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                  Alert History
                </h4>
                <div className="space-y-2">
                  {detail.recentAlerts.map((alert, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <StatusBadge status={alert.tier || 'watch'} />
                        <span className="text-sm text-gray-700">
                          {alert.consecutiveWorkingDays} days
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(alert.triggeredAt).toLocaleDateString('en-SG', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                        <span className="ml-2">
                          <StatusBadge
                            status={alert.alertStatus === 'active' ? 'in_progress' : 'acknowledged'}
                            label={alert.alertStatus === 'active' ? 'Active' : 'Acknowledged'}
                          />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
    </Modal>
  );
};

export default EmployeeRestDayDetailModal;
